import { lazy } from "react";
import { Navigate } from 'react-router-dom';

const LabLayout = lazy(() => import('../Layout/Lab'));
const Login = lazy(() => import('../Pages/Login'));
const ListLabOrders = lazy(() => import('../Pages/LabOrders/List'));
const LabOrderDetail = lazy(() => import('../Pages/LabOrders/Detail'));
const CreateLabOrder = lazy(() => import('../Pages/LabOrders/Create'));
const EditLabOrder = lazy(() => import('../Pages/LabOrders/Edit'));
const CustomerList = lazy(() => import('../Pages/Customers/CustomerList'));
const CustomerDetail = lazy(() => import('../Pages/Customers/CustomerDetail'));
const CustomerSettings = lazy(() => import('../Pages/Customers/CustomerSettings'));
const CustomerLensBilling = lazy(() => import('../Pages/Customers/CustomerLensBilling'));
const CustomerStorage = lazy(() => import('../Pages/Customers/CustomerStorage'));
const CustomerLenses = lazy(() => import('../Pages/Customers/CustomerLenses'));
const CustomerInvoicing = lazy(() => import('../Pages/Customers/CustomerInvoicing'));
const IntegrationsPage = lazy(() => import('../Pages/Integrations'));
const OpticalLabsPage = lazy(() => import('../Pages/OpticalLabs'));
const LensFlowPage = lazy(() => import('../Pages/LensFlow'));
const CreateFlowPage = lazy(() => import('../Pages/CreateFlow'));
const EditFlowPage = lazy(() => import('../Pages/EditFlow'));
const LabProfilePage = lazy(() => import('../Pages/LabProfile'));
const InventoryDashboard = lazy(() => import('../Pages/Inventory/InventoryDashboard'));
const InventoryItems = lazy(() => import('../Pages/Inventory/InventoryItems'));
const InventoryItemsDetail = lazy(() => import('../Pages/Inventory/InventoryItemsDetail'));
const InventoryItemsEvents = lazy(() => import('../Pages/Inventory/InventoryItemsEvents'));
const InventoryItemsAllocated = lazy(() => import('../Pages/Inventory/InventoryItemsAllocated'));
const InventoryFrames = lazy(() => import('../Pages/Inventory/InventoryFrames'));
const InventoryFramesDetail = lazy(() => import('../Pages/Inventory/InventoryFramesDetail'));
const InventoryLocations = lazy(() => import('../Pages/Inventory/InventoryLocations'));
const InventoryStorage = lazy(() => import('../Pages/Inventory/InventoryStorage'));
const InventoryReturns = lazy(() => import('../Pages/Inventory/InventoryReturns'));
const InventoryReturn = lazy(() => import('../Pages/Inventory/InventoryReturn'));
const CustomerInventoryPage = lazy(() => import('../Pages/Customers/CustomerInventoryPage'));
const CustomerReturnsPage = lazy(() => import('../Pages/Customers/CustomerReturnsPage'));
const LensDeskPage = lazy(() => import('../Pages/Lenses/LensDesk'));
const HotCellsPage = lazy(() => import('../Pages/Lenses/HotCells'));
const ReplenishmentPage = lazy(() => import('../Pages/Lenses/Replenishment'));
const OrderPlanDetailPage = lazy(() => import('../Pages/Lenses/OrderPlanDetail'));
const LensCatalogPage = lazy(() => import('../Pages/Lenses/LensCatalog'));
const LensAttributesPage = lazy(() => import('../Pages/Lenses/LensAttributes'));
const LensDetailPage = lazy(() => import('../Pages/Lenses/LensDetail'));
const LensDetailCustomersPage = lazy(() => import('../Pages/Lenses/LensDetailCustomers'));
const LensDetailEventsPage = lazy(() => import('../Pages/Lenses/LensDetailEvents'));
const LensDetailAllocatedPage = lazy(() => import('../Pages/Lenses/LensDetailAllocated'));
const LensSuppliersPage = lazy(() => import('../Pages/Lenses/LensSuppliers'));
const LensSupplierDetailPage = lazy(() => import('../Pages/Lenses/LensSupplierDetail'));
const LensSupplierSettingsPage = lazy(() => import('../Pages/Lenses/LensSupplierSettings'));
const LensSupplierPriceFeedsPage = lazy(() => import('../Pages/Lenses/LensSupplierPriceFeeds'));
const LensFeedImportPage = lazy(() => import('../Pages/Lenses/LensFeedImport'));
const StockCountPage = lazy(() => import('../Pages/Lenses/StockCount'));
const LensPurchaseOrdersPage = lazy(() => import('../Pages/Lenses/LensPurchaseOrders'));
const LensPurchaseOrderDetailPage = lazy(() => import('../Pages/Lenses/LensPurchaseOrderDetail'));
const LensPurchaseOrderCreatePage = lazy(() => import('../Pages/Lenses/LensPurchaseOrderCreate'));
const ExpensesPage = lazy(() => import('../Pages/Expenses/Expenses'));
const NotificationsPage = lazy(() => import('../Pages/Notifications/Notifications'));
const StatusAlertSettingsPage = lazy(() => import('../Pages/Notifications/StatusAlertSettings'));
const ExpenseCreatePage = lazy(() => import('../Pages/Expenses/ExpenseCreate'));
const ExpenseDetailPage = lazy(() => import('../Pages/Expenses/ExpenseDetail'));
const ReportsPage = lazy(() => import('../Pages/Reports'));
const DaysAtLabPage = lazy(() => import('../Pages/Reports/DaysAtLab'));
const JobsInProcessPage = lazy(() => import('../Pages/Reports/JobsInProcess'));
const InventoryEventsReportPage = lazy(() => import('../Pages/Reports/InventoryEvents'));
const BillingARReportPage = lazy(() => import('../Pages/Reports/BillingAR'));
const SpendReportPage = lazy(() => import('../Pages/Reports/Spend'));
const ShippingCarriersPage = lazy(() => import('../Pages/ShippingCarriers'));
const ShippingLabelsPage = lazy(() => import('../Pages/ShippingLabels'));
const VaultPage = lazy(() => import('../Pages/Vault'));
const NotFoundPage = lazy(() => import('../Pages/NotFound'));
const InventoryReportPage = lazy(() => import('../Pages/InventoryReport'));

// Public merchant-facing inventory report — reachable with or without a session
const publicRoutes = [
    { path: '/inventory-report/:reportKey', element: <InventoryReportPage /> },
];

const routes = ({ isAuthenticated }) => {
    if (!isAuthenticated) {
        return [
            ...publicRoutes,
            { path: '/login', element: <Login /> },
            { path: '*', element: <Navigate to="/login" /> }
        ];
    }

    return [...publicRoutes, {
        path: '/',
        element: <LabLayout />,
        children: [
            { path: 'profile', element: <LabProfilePage /> },
            { path: 'lens-flow', element: <LensFlowPage /> },
            { path: 'lens-flow/create/', element: <CreateFlowPage /> },
            { path: 'lens-flow/edit/:id', element: <EditFlowPage /> },
            { path: 'orders', element: <ListLabOrders /> },
            { path: 'orders/new', element: <CreateLabOrder /> },
            { path: 'orders/:labOrderId', element: <LabOrderDetail /> },
            { path: 'orders/:labOrderId/edit', element: <EditLabOrder /> },
            { path: 'customers', element: <CustomerList /> },
            { path: 'customers/:customerId/customer', element: <CustomerDetail /> },
            { path: 'customers/:customerId/settings', element: <CustomerSettings /> },
            { path: 'customers/:customerId/lenses', element: <CustomerLenses /> },
            { path: 'customers/:customerId/invoicing', element: <CustomerInvoicing /> },
            { path: 'customers/:customerId/lens-billing', element: <CustomerLensBilling /> },
            { path: 'customers/:customerId/inventory', element: <CustomerInventoryPage /> },
            { path: 'customers/:customerId/returns', element: <CustomerReturnsPage /> },
            { path: 'customers/:customerId/storage', element: <CustomerStorage /> },
            { path: 'lenses', element: <LensDeskPage /> },
            { path: 'lenses/hot-cells', element: <HotCellsPage /> },
            { path: 'lenses/replenishment', element: <ReplenishmentPage /> },
            { path: 'lenses/replenishment/:tab', element: <ReplenishmentPage /> },
            { path: 'lenses/planner', element: <OrderPlanDetailPage /> },
            { path: 'lenses/planner/:planId', element: <OrderPlanDetailPage /> },
            { path: 'lenses/catalog', element: <LensCatalogPage /> },
            { path: 'lenses/catalog/:lensId', element: <LensDetailPage /> },
            { path: 'lenses/catalog/:lensId/customers', element: <LensDetailCustomersPage /> },
            { path: 'lenses/catalog/:lensId/events', element: <LensDetailEventsPage /> },
            { path: 'lenses/catalog/:lensId/allocated', element: <LensDetailAllocatedPage /> },
            { path: 'lenses/attributes', element: <LensAttributesPage /> },
            { path: 'lenses/suppliers', element: <LensSuppliersPage /> },
            { path: 'lenses/suppliers/:supplierId', element: <LensSupplierDetailPage /> },
            { path: 'lenses/suppliers/:supplierId/settings', element: <LensSupplierSettingsPage /> },
            { path: 'lenses/suppliers/:supplierId/feeds', element: <LensSupplierPriceFeedsPage /> },
            { path: 'lenses/suppliers/:supplierId/feeds/:feedId/import/:versionId', element: <LensFeedImportPage /> },
            { path: 'lenses/stock-count', element: <StockCountPage /> },
            { path: 'lenses/purchase-orders', element: <LensPurchaseOrdersPage /> },
            { path: 'lenses/purchase-orders/new', element: <LensPurchaseOrderCreatePage /> },
            { path: 'lenses/purchase-orders/:poId', element: <LensPurchaseOrderDetailPage /> },
            { path: 'lenses/purchase-orders/:poId/edit', element: <LensPurchaseOrderCreatePage /> },
            { path: 'notifications', element: <NotificationsPage /> },
            { path: 'notifications/status-alerts', element: <StatusAlertSettingsPage /> },
            { path: 'expenses', element: <ExpensesPage /> },
            { path: 'expenses/new', element: <ExpenseCreatePage /> },
            { path: 'expenses/:expenseId', element: <ExpenseDetailPage /> },
            { path: 'expenses/:expenseId/edit', element: <ExpenseCreatePage /> },
            { path: 'reports', element: <Navigate to="/reports/overview" /> },
            { path: 'reports/overview', element: <ReportsPage /> },
            { path: 'reports/days-at-lab', element: <DaysAtLabPage /> },
            { path: 'reports/jobs-in-process', element: <JobsInProcessPage /> },
            { path: 'reports/inventory-events', element: <InventoryEventsReportPage /> },
            { path: 'reports/billing-ar', element: <BillingARReportPage /> },
            { path: 'reports/spend', element: <SpendReportPage /> },
            { path: 'inventory', element: <InventoryDashboard /> },
            { path: 'inventory/items', element: <InventoryItems /> },
            { path: 'inventory/items/:itemId', element: <InventoryItemsDetail /> },
            { path: 'inventory/items/:itemId/events', element: <InventoryItemsEvents /> },
            { path: 'inventory/items/:itemId/allocated', element: <InventoryItemsAllocated /> },
            { path: 'inventory/frames', element: <InventoryFrames /> },
            { path: 'inventory/frames/:frameId', element: <InventoryFramesDetail /> },
            { path: 'inventory/locations', element: <InventoryLocations /> },
            { path: 'inventory/storage', element: <InventoryStorage /> },
            { path: 'inventory/returns', element: <InventoryReturns /> },
            { path: 'inventory/returns/new', element: <InventoryReturn /> },
            { path: 'inventory/returns/:returnId', element: <InventoryReturn /> },
            { path: 'integrations', element: <IntegrationsPage /> },
            { path: 'vault', element: <VaultPage /> },
            { path: 'optical-labs', element: <OpticalLabsPage /> },
            { path: 'shipping', element: <Navigate to="/shipping/carriers" /> },
            { path: 'shipping/carriers', element: <ShippingCarriersPage /> },
            { path: 'shipping/labels', element: <ShippingLabelsPage /> },
            { path: 'shipping-carriers', element: <Navigate to="/shipping/carriers" /> },
            { path: '', element: <Navigate to="/orders" /> },
            { path: '*', element: <NotFoundPage /> }
        ]
    }];
};

export default routes;
